'use client';

import { FieldFooter } from '@lichtblick/photon/atoms/field-footer';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ExclamationMarkTriangleXs } from '@lichtblick/photon/icons/symbols/exclamation-mark-triangle-xs';
import { type FC, useState } from 'react';
import {
  IS_INTERESTED_IN_HEAT_PUMP,
  IS_INTERESTED_IN_PV,
  IS_INTERESTED_IN_STROM_WALLET,
  IS_INTERESTED_IN_WALLBOX,
} from '@/app/energieloesungen-checkout/_product-interest/product-interest.const';
import { FormBox } from '@/components/form-box/form-box';
import { SubmitButton } from '@/components/form-box/submit-button';
import { CheckboxCard } from './checkbox-card';
import styles from './product-interest.module.css';

interface ProductInterestProps {
  interestDefaults: {
    interestInPv: boolean;
    interestInHeatPump: boolean;
    interestInWallBox: boolean;
    interestInStromWallet: boolean;
  };
  submitAction: (_state: undefined, formData: FormData) => Promise<undefined>;
}
interface ProductInterestState {
  interestInPv: boolean;
  interestInHeatPump: boolean;
  interestInWallBox: boolean;
  interestInStromWallet: boolean;
  hasError: boolean;
}

const togglePv = (prev: ProductInterestState): ProductInterestState => {
  if (prev.interestInPv === false) {
    return { ...prev, interestInPv: true, hasError: false };
  }

  const stateUpdate: Partial<ProductInterestState> = {
    interestInPv: false,
    interestInStromWallet: false,
  };

  if (!prev.interestInHeatPump) {
    stateUpdate.hasError = true;
    stateUpdate.interestInWallBox = false;
  }

  return { ...prev, ...stateUpdate };
};

const toggleHeatPump = (prev: ProductInterestState): ProductInterestState => {
  if (prev.interestInHeatPump === false) {
    return { ...prev, interestInHeatPump: true, hasError: false };
  }
  const stateUpdate: Partial<ProductInterestState> = {
    interestInHeatPump: false,
  };
  if (!prev.interestInPv) {
    stateUpdate.hasError = true;
    stateUpdate.interestInWallBox = false;
  }
  return { ...prev, ...stateUpdate };
};

export const ProductInterest: FC<ProductInterestProps> = ({ interestDefaults, submitAction }) => {
  const [state, setState] = useState({ ...interestDefaults, hasError: false });

  return (
    <FormBox action={submitAction} className={styles.form}>
      <CheckboxCard forcedError={state.hasError}>
        <CheckboxCard.Option
          label="Solaranlage"
          dataTestId={`checkbox-${IS_INTERESTED_IN_PV}`}
          footer="mit / ohne Batteriespeicher"
        >
          <CheckboxCard.Checkbox
            checked={state.interestInPv}
            onInvalid={() => {
              setState((prev) => ({ ...prev, hasError: true }));
            }}
            onChange={() => setState(togglePv)}
            name={IS_INTERESTED_IN_PV}
            value="true"
            required={!state.interestInHeatPump}
            forcedError={state.hasError}
          />
        </CheckboxCard.Option>
        <CheckboxCard.Option
          footer="Dynamischer Tarif mit intelligenter Steuerung"
          label="StromWallet"
          dataTestId={`checkbox-${IS_INTERESTED_IN_STROM_WALLET}`}
        >
          <CheckboxCard.Checkbox
            checked={state.interestInStromWallet}
            onChange={() => setState((prev) => ({ ...prev, interestInStromWallet: !prev.interestInStromWallet }))}
            name={IS_INTERESTED_IN_STROM_WALLET}
            disabled={state.interestInPv === false}
            value="true"
          />
        </CheckboxCard.Option>
      </CheckboxCard>

      <CheckboxCard data-testid={`checkbox-${IS_INTERESTED_IN_HEAT_PUMP}`} forcedError={state.hasError}>
        <CheckboxCard.Option footer="Mit Strom günstiger heizen" label="Wärmepumpe">
          <CheckboxCard.Checkbox
            checked={state.interestInHeatPump}
            onChange={() => setState(toggleHeatPump)}
            onInvalid={() => setState((prev) => ({ ...prev, hasError: true }))}
            name={IS_INTERESTED_IN_HEAT_PUMP}
            value="true"
            required={!state.interestInPv}
            forcedError={state.hasError}
          />
        </CheckboxCard.Option>
      </CheckboxCard>
      {state.hasError && (
        <FieldFooter
          className={styles['error-message']}
          data-testid="field-error"
          error
          icon={<ExclamationMarkTriangleXs />}
        >
          Hier fehlt noch eine Angabe
        </FieldFooter>
      )}

      <div>
        <Text className={styles['add-on-text']} size="s" renderAs="div" bold>
          Add-on
        </Text>

        <CheckboxCard data-testid={`checkbox-${IS_INTERESTED_IN_WALLBOX}`}>
          <CheckboxCard.Option footer="Elektrofahrzeug zuhause laden" label="Wallbox">
            <CheckboxCard.Checkbox
              checked={state.interestInWallBox}
              disabled={state.interestInPv === false && state.interestInHeatPump === false}
              onChange={() => setState((prev) => ({ ...prev, interestInWallBox: !prev.interestInWallBox }))}
              name={IS_INTERESTED_IN_WALLBOX}
              value="true"
            />
          </CheckboxCard.Option>
        </CheckboxCard>
      </div>

      <SubmitButton />
    </FormBox>
  );
};
